import { isWorkflowReadOnly } from '../utils/workflowAccess';
import { Dialog, TextField } from '../components/FarChat/ChatFeedbackOverlays';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Paper, Snackbar, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createManualActivityEvent, getContractSchedule, getTrackedContracts, updateManualActivityEvent, type ActivityEvent, type ContractSchedule, type ManualActivityEventInput, type Suspense, type TrackedContract } from '../api/trackerFunctions';
import { groupRemindersByOptionPeriod, sortActivityEventsNewestFirst } from '../utils/trackerViewModel';
import { toUserFacingError } from '../utils/userFacingError';
import ContractScheduleGantt from '../components/ContractScheduleGantt';

interface ContractDetailProps {
  contractId: string;
  onBack?: () => void;
}

const display = (value: unknown) => value === null || value === undefined || value === '' ? '—' : String(value);
const money = (value: number | null) => value === null || value === undefined
  ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
const urgencyColor = { red: 'error.main', orange: 'warning.main', yellow: 'warning.main', green: 'success.main' } as const;
const countdown = (days: number) => days < 0
  ? `${Math.abs(days)} days overdue` : days === 0 ? 'Due today' : `in ${days} days`;

const activityTypeLabels: Record<string, string> = {
  dd2579: 'DD2579',
  compliance_review: 'Compliance review',
  legal_review: 'Legal review',
  business_clearance: 'Business clearance',
  contract_clearance: 'Contract clearance',
  contract_record_saved: 'Contract record saved',
  tracker_handoff_completed: 'Tracker handoff',
  administrative_note: 'Administrative note',
};

const activityLabel = (value: string) => activityTypeLabels[value]
  ?? value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
const outcomeLabel = (value: string) => value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
const localDateTimeNow = () => new Date().toISOString().slice(0, 16);
const formatOccurredAt = (value: string | null) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp)
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(timestamp))
    : null;
};

const toDateTimeLocal = (value: string | null) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  if (!Number.isFinite(timestamp)) return localDateTimeNow();
  const date = new Date(timestamp);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const isManualActivity = (event: ActivityEvent) => event.workflowStage === 'manual';

const mergeActivityEvents = (...sets: (ActivityEvent[] | null | undefined)[]): ActivityEvent[] => {
  const merged = new Map<string, ActivityEvent>();
  sets.flatMap(set => set ?? []).forEach(event => merged.set(event.eventId, event));
  return Array.from(merged.values());
};

const upsertActivityEvent = (events: ActivityEvent[] | null | undefined, event: ActivityEvent): ActivityEvent[] => [
  event,
  ...(events ?? []).filter(existing => existing.eventId !== event.eventId),
];

function ActivityEventRow({ event, onEdit, readOnly }: { readOnly: boolean; event: ActivityEvent; onEdit: (event: ActivityEvent) => void }) {
  const occurredAt = formatOccurredAt(event.occurredAt);
  const updatedAt = formatOccurredAt(event.updatedAt);
  const manual = isManualActivity(event);
  return (
    <Box component="li" sx={{ listStyle: 'none', display: 'grid', gridTemplateColumns: '3px minmax(0, 1fr)', minWidth: 0, bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: 'primary.main' }} />
      <Box sx={{ minWidth: 0, px: 1.5, py: 1.25 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{activityLabel(event.activityType)}</Typography>
            {event.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>{event.note}</Typography>}
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}>
            {manual && <Chip size="small" label="Manually entered" variant="outlined" />}
            {event.status && <Chip size="small" label={outcomeLabel(event.status)} />}
            {manual && !readOnly && <Button size="small" onClick={() => onEdit(event)} aria-label="Edit activity">Edit</Button>}
          </Stack>
        </Stack>
        {(occurredAt || event.performedBy || event.recordedBy || event.artifactLink) && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
            {occurredAt && <Typography variant="caption" color="text.secondary">{occurredAt}</Typography>}
            {event.performedBy && <Typography variant="caption" color="text.secondary">Performed by {event.performedBy}{event.performedByRole ? ` · ${event.performedByRole}` : ''}</Typography>}
            {event.recordedBy && <Typography variant="caption" color="text.secondary">Recorded by {event.recordedBy}</Typography>}
            {event.artifactLink && <Typography component="a" variant="caption" href={event.artifactLink} target="_blank" rel="noreferrer" sx={{ overflowWrap: 'anywhere' }}>Open artifact</Typography>}
            {event.updatedBy && <Typography variant="caption" color="text.secondary">Revised by {event.updatedBy}{updatedAt ? ` · ${updatedAt}` : ''}</Typography>}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function ReminderCard({ reminder }: { reminder: Suspense }) {
  return (
    <Paper variant="outlined" sx={{ display: 'grid', gridTemplateColumns: '4px 1fr auto', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box aria-label={`${reminder.urgency[0].toUpperCase()}${reminder.urgency.slice(1)} urgency`} sx={{ bgcolor: urgencyColor[reminder.urgency] }} />
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{reminder.title}</Typography>
        <Typography variant="caption" color="text.secondary">Due {reminder.dueDate} · {countdown(reminder.daysRemaining)}</Typography>
        {reminder.urgency === 'yellow' && <Typography variant="caption" sx={{ display: 'block', color: urgencyColor.yellow, fontWeight: 700 }}>Yellow urgency</Typography>}
      </Box>
      <Chip size="small" label={reminder.status.replace('_', ' ')} sx={{ alignSelf: 'center', mr: 1 }} />
    </Paper>
  );
}

export default function ContractDetail({ contractId, onBack }: ContractDetailProps) {
  const [contract, setContract] = useState<TrackedContract | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<ContractSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activitySuccess, setActivitySuccess] = useState<string | null>(null);
  const [editingActivity, setEditingActivity] = useState<ActivityEvent | null>(null);
  const [activityForm, setActivityForm] = useState<ManualActivityEventInput>({
    activityType: 'administrative_note', status: 'completed', performedBy: '', occurredAt: localDateTimeNow(), note: '',
  });

  useEffect(() => {
    let active = true;
    setContract(null);
    setSchedule(null);
    setError(null);
    setScheduleError(null);
    setLoaded(false);
    setScheduleLoading(true);
    setActivityDialogOpen(false);
    setEditingActivity(null);
    setActivityError(null);
    setActivitySuccess(null);
    getTrackedContracts().then(payload => {
      if (!active) return;
      setContract(payload.contracts.find(item => item.flowId === contractId || item.record.contract_number === contractId) ?? null);
      setLoaded(true);
    }).catch(err => {
      if (!active) return;
      setError(toUserFacingError(err));
      setLoaded(true);
    });
    getContractSchedule(contractId).then(data => {
      if (!active) return;
      setSchedule(data);
      setScheduleLoading(false);
    }).catch(err => {
      if (!active) return;
      setScheduleError(toUserFacingError(err));
      setScheduleLoading(false);
    });
    return () => { active = false; };
  }, [contractId]);

  const readOnly = isWorkflowReadOnly(contract ?? schedule);
  const accessRef = React.useRef(readOnly); accessRef.current = readOnly;
  const openActivityDialog = () => {
    if (accessRef.current) return;
    setEditingActivity(null);
    setActivityForm({ activityType: 'administrative_note', status: 'completed', performedBy: '', occurredAt: localDateTimeNow(), note: '' });
    setActivityError(null);
    setActivityDialogOpen(true);
  };

  const openEditActivityDialog = (event: ActivityEvent) => {
    if (accessRef.current) return;
    if (!isManualActivity(event)) return;
    setEditingActivity(event);
    setActivityForm({
      activityType: event.activityType,
      status: event.status,
      performedBy: event.performedBy ?? '',
      occurredAt: toDateTimeLocal(event.occurredAt),
      note: event.note ?? '',
    });
    setActivityError(null);
    setActivityDialogOpen(true);
  };

  const saveActivity = async () => {
    if (accessRef.current || !contract || activitySaving) return;
    const performedBy = activityForm.performedBy.trim();
    if (!performedBy) {
      setActivityError('Enter who performed this activity.');
      return;
    }
    const timestamp = Date.parse(activityForm.occurredAt);
    if (!Number.isFinite(timestamp)) {
      setActivityError('Enter a valid occurred date and time.');
      return;
    }
    const occurredAt = new Date(timestamp).toISOString();
    const note = activityForm.note?.trim() || null;
    if (editingActivity) {
      setActivitySaving(true);
      setActivityError(null);
      try {
        const updated = await updateManualActivityEvent(contract.flowId, editingActivity.eventId, {
          activityType: activityForm.activityType,
          status: activityForm.status,
          performedBy,
          occurredAt,
          note,
        });
        setContract(current => current ? {
          ...current,
          activityEvents: upsertActivityEvent(current.activityEvents, updated),
        } : current);
        setSchedule(current => current ? { ...current, activityEvents: upsertActivityEvent(current.activityEvents, updated) } : current);
        setActivityDialogOpen(false);
        setEditingActivity(null);
        setActivitySuccess('Activity updated.');
      } catch (activitySaveError) {
        setActivityError(toUserFacingError(activitySaveError));
      } finally {
        setActivitySaving(false);
      }
      return;
    }
    setActivitySaving(true);
    setActivityError(null);
    try {
      const created = await createManualActivityEvent(contract.flowId, {
        ...activityForm,
        performedBy,
        occurredAt,
        note,
      });
      setContract(current => current ? { ...current, activityEvents: [created, ...(current.activityEvents ?? [])] } : current);
      setSchedule(current => current ? { ...current, activityEvents: upsertActivityEvent(current.activityEvents, created) } : current);
      setActivityDialogOpen(false);
      setActivitySuccess('Activity recorded.');
    } catch (activitySaveError) {
      setActivityError(toUserFacingError(activitySaveError));
    } finally {
      setActivitySaving(false);
    }
  };

  if (!loaded) return <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading contract detail" /></Box>;
  if (error) return <Box sx={{ maxWidth: 1200, mx: 'auto', p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!contract && !schedule) return <Box sx={{ maxWidth: 1200, mx: 'auto', p: 4 }}><Alert severity="warning">Contract not found.</Alert></Box>;

  const record = contract?.record ?? {
    contract_number: schedule?.display.contractNumber ?? '', contract_name: schedule?.display.contractName ?? schedule?.display.workflowName ?? '',
  } as TrackedContract['record'];
  const optionPeriods = contract?.optionPeriods ?? schedule?.optionPeriods ?? [];
  const suspenses = contract?.suspenses ?? schedule?.suspenses ?? [];
  const activitySource = mergeActivityEvents(schedule?.activityEvents, contract?.activityEvents);
  const scheduleForDisplay = schedule ? { ...schedule, activityEvents: activitySource } : null;
  const remindersByPeriod = groupRemindersByOptionPeriod(optionPeriods, suspenses);
  const openReminderCount = Object.values(remindersByPeriod)
    .flat()
    .filter(item => item.status !== 'complete').length;
  const overview = [
    ['Contract number', record.contract_number], ['Status', record.contract_status],
    ['Contracting officer', record.co], ['Contract specialist', record.cs],
    ['Customer', record.customer], ['Customer POC', record.customer_poc],
    ['Contractor POC', record.ktr_poc], ['Total award value', money(record.total_award_value)],
    ['Contract start', record.contract_start_date], ['Contract end', record.contract_end_date],
  ];
  const activityEvents = sortActivityEventsNewestFirst(activitySource);

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
      {onBack && <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>Back to tracker</Button>}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary">{record.contract_number}</Typography>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>{record.contract_name || 'Unnamed contract'}</Typography>
          <Typography color="text.secondary">{record.customer || 'Customer not recorded'} · Owned by {contract?.ownerEmail || '—'}</Typography>
        </Box>
      </Stack>

      <Box sx={{ width: '100%', minWidth: 0, mb: 2 }}>
        {scheduleLoading && <Paper variant="outlined" sx={{ p: 3 }} role="status"><CircularProgress size={20} aria-label="Loading contract schedule" /><Typography component="span" sx={{ ml: 1 }}>Loading contract schedule…</Typography></Paper>}
        {!scheduleLoading && scheduleError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { setScheduleLoading(true); setScheduleError(null); getContractSchedule(contractId).then(setSchedule).catch(err => setScheduleError(toUserFacingError(err))).finally(() => setScheduleLoading(false)); }}>Retry</Button>}>Contract schedule unavailable.</Alert>}
        {!scheduleLoading && !scheduleError && scheduleForDisplay && <ContractScheduleGantt schedule={scheduleForDisplay} />}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' }, gap: 2 }}>
        <Stack spacing={2}>
          <Paper data-testid="activity-milestones-panel" variant="outlined" sx={{ p: 3, minWidth: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" component="h2">Activity &amp; milestones</Typography>
                <Typography variant="body2" color="text.secondary">Recorded workflow actions and contract milestones.</Typography>
              </Box>
              {contract && !readOnly && <Button variant="outlined" size="small" onClick={openActivityDialog} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>Log activity</Button>}
            </Stack>
            {activityEvents.length > 0 ? (
              <Stack component="ol" spacing={1} sx={{ m: 0, p: 0 }}>
                {activityEvents.map(event => <ActivityEventRow key={event.eventId} event={event} readOnly={readOnly} onEdit={openEditActivityDialog} />)}
              </Stack>
            ) : (
              <Typography color="text.secondary">Activity will appear here when new workflow actions are recorded.</Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>Overview</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {overview.map(([label, value]) => <Box key={label}><Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{label}</Typography><Typography>{display(value)}</Typography></Box>)}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>Option periods</Typography>
            <Stack divider={<Divider flexItem />}>
              {optionPeriods.map((period, index) => {
                const reminders = remindersByPeriod[period.optionPeriodId] ?? [];
                return (
                  <Stack data-testid={`option-period-${period.optionPeriodId}`} key={`${period.optionPeriodId || period.label}-${index}`} spacing={1} sx={{ py: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Box><Typography sx={{ fontWeight: 650 }}>{period.label}</Typography><Typography variant="body2" color="text.secondary">{display(period.start)} — {display(period.end)}</Typography></Box>
                      <Chip size="small" label={period.status} color={period.status.toLowerCase() === 'current' ? 'primary' : 'default'} />
                    </Stack>
                    {reminders.map(reminder => <ReminderCard key={`${reminder.eventKey}-${reminder.dueDate}`} reminder={reminder} />)}
                  </Stack>
                );
              })}
              {optionPeriods.length === 0 && <Typography color="text.secondary">No option periods are available.</Typography>}
            </Stack>
          </Paper>

          {['Funding & burn', 'Key personnel', 'Recent activity'].map(title => (
            <Paper variant="outlined" sx={{ p: 3 }} key={title}>
              <Typography variant="h6">{title}</Typography>
              <Typography color="text.secondary">Coming soon</Typography>
            </Paper>
          ))}
        </Stack>

        <Paper component="aside" variant="outlined" sx={{ p: 3, alignSelf: 'start', position: { lg: 'sticky' }, top: 16 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>Upcoming suspenses</Typography>
          {openReminderCount > 0
            ? <Typography color="text.secondary">{openReminderCount} upcoming option {openReminderCount === 1 ? 'suspense is' : 'suspenses are'} linked to their period above.</Typography>
            : <Typography color="text.secondary">No upcoming option suspenses.</Typography>}
        </Paper>
      </Box>

      <Dialog
        open={activityDialogOpen}
        onClose={() => { if (!activitySaving) { setActivityDialogOpen(false); setEditingActivity(null); } }}
        aria-labelledby="activity-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="activity-dialog-title">{editingActivity ? 'Edit activity' : 'Log activity'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          {activityError && <Alert severity="error">{activityError}</Alert>}
          <TextField select label="Activity type" value={activityForm.activityType} onChange={event => setActivityForm(current => ({ ...current, activityType: event.target.value }))} fullWidth>
            <MenuItem value="administrative_note">Administrative note</MenuItem>
            <MenuItem value="dd2579">DD2579</MenuItem>
            <MenuItem value="compliance_review">Compliance review</MenuItem>
            <MenuItem value="business_clearance">Business clearance</MenuItem>
            <MenuItem value="contract_clearance">Contract clearance</MenuItem>
            <MenuItem value="contract_record_saved">Contract record saved</MenuItem>
          </TextField>
          <TextField select label="Outcome/status" value={activityForm.status} onChange={event => setActivityForm(current => ({ ...current, status: event.target.value }))} fullWidth>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="in_progress">In progress</MenuItem>
            <MenuItem value="requested">Requested</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="returned">Returned</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="not_applicable">Not applicable</MenuItem>
          </TextField>
          <TextField
            label="Performed by"
            value={activityForm.performedBy}
            onChange={event => setActivityForm(current => ({ ...current, performedBy: event.target.value }))}
            required
            autoComplete="name"
            fullWidth
            inputProps={{ maxLength: 200 }}
          />
          <TextField label="Occurred date/time" type="datetime-local" value={activityForm.occurredAt} onChange={event => setActivityForm(current => ({ ...current, occurredAt: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Note" value={activityForm.note ?? ''} onChange={event => setActivityForm(current => ({ ...current, note: event.target.value }))} multiline minRows={2} fullWidth inputProps={{ maxLength: 2000 }} />
          <Typography variant="caption" color="text.secondary">The server records who entered this activity separately.</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={readOnly || activitySaving} onClick={() => { setActivityDialogOpen(false); setEditingActivity(null); }}>Cancel</Button>
          <Button variant="contained" disabled={readOnly || activitySaving} onClick={saveActivity} aria-label={activitySaving ? 'Saving activity' : editingActivity ? 'Save changes' : 'Save activity'}>{activitySaving ? 'Saving activity' : editingActivity ? 'Save changes' : 'Save activity'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(activitySuccess)} autoHideDuration={4000} onClose={() => setActivitySuccess(null)} message={activitySuccess} />
    </Box>
  );
}
